import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import InfoPage from "../InfoPage";
import { CHECKOUT_TRIAL_TOOLTIP } from "../../utils/checkoutTrialTooltip";
import useTrustedCarouselProfiles from "../../hooks/useTrustedCarouselProfiles";
import IntroVideoPreview from "../../components/IntroVideoPreview";
import ExitIntentModal from "../../components/ExitIntentModal";
import './Success.sass';

const PlanCard = ({ title, price, badge, description, planId }) => (
  <div className="plan-card">
    {badge && (
      <div className="plan-badge">{badge}</div>
    )}
    <div className="plan-title">{title}</div>
    <div className="plan-price">{price}</div>
    <ul className="plan-features">
      {(description || []).map((line, idx) => (
        <li key={idx}>
          <span style={{ color: '#0f084b' }}>•</span>
          <span>{line}</span>
        </li>
      ))}
    </ul>
    <div className="plan-footer">Plus processing fees.</div>
    <Tippy content={CHECKOUT_TRIAL_TOOLTIP} placement="top" maxWidth={380}>
      <div style={{ width: '100%' }}>
        <button type="button" className="plan-button" onClick={async () => {
          try {
            const params = new URLSearchParams(window.location.search);
            let email = params.get('email');
            if (!email) {
              try {
                email = localStorage.getItem('postaway_email') || '';
              } catch (_) {}
            }
            if (!email) { alert('Missing email. Please go back to onboarding.'); return; }
            const rwRef = (window.Rewardful && window.Rewardful.referral) ||
              (document.cookie.match(/(?:^|;)\s*rw_ref=([^;]+)/)?.[1] ?? null);

            const res = await fetch('/api/stripe/checkout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, planId, rwRef }) });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error('stripe_checkout_error');
            window.location.assign(data.url);
          } catch (e) {
            // eslint-disable-next-line no-alert
            alert('Unable to start checkout. Please try again.');
          }
        }}>
          Continue
        </button>
      </div>
    </Tippy>
  </div>
);

const Success = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const trustedProfiles = useTrustedCarouselProfiles();
  const profileCount = Math.max(trustedProfiles.length, 1);
  const checkoutDesktopDurationSeconds = Math.min(520, Math.max(52, Math.round(profileCount * 3.8)));
  const checkoutMobileDurationSeconds = Math.min(620, Math.max(68, Math.round(profileCount * 4.6)));
  const checkoutCarouselTimingVars = {
    "--checkout-carousel-duration-desktop": `${checkoutDesktopDurationSeconds}s`,
    "--checkout-carousel-duration-mobile": `${checkoutMobileDurationSeconds}s`,
  };

  // Gatekeeping: Ensure user has email before showing checkout page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let email = params.get('email');
    if (!email) {
      try {
        email = localStorage.getItem('postaway_email') || '';
      } catch (_) {}
    }

    if (!email) {
      // No email - redirect to creator login
      console.warn('Success page: No email found, redirecting to login');
      navigate('/creator-login');
      return;
    }

    // Email exists - allow access
    setAuthorized(true);
  }, [navigate]);

  // Don't render until authorized
  if (!authorized) {
    return null;
  }

  const header = (
    <div className="success-page">
      <div className="pricing-header">
        <h3>Choose Your Creator Plan</h3>
        <p>Select the membership that best fits your creator journey.</p>
        <div className="trusted-carousel" aria-label="Trusted hosts and creators">
          <div className="trusted-carousel-title">Trusted by top travel creators, photographers and hosts in the hospitality industry.</div>
          <div className="trusted-carousel-viewport">
            <div className="trusted-carousel-track" style={checkoutCarouselTimingVars}>
              {[...trustedProfiles, ...trustedProfiles].map((profile, idx) => (
                <div className="trusted-carousel-item" key={`${profile.handle}-${idx}`}>
                  {profile.introVideoUrl ? (
                    <IntroVideoPreview
                      className="trusted-carousel-avatar"
                      mode="card"
                      posterUrl={profile.introVideoPosterUrl || profile.image}
                      publicId={profile.introVideoPublicId}
                      fullUrl={profile.introVideoUrl}
                      fallbackImageUrl={profile.image}
                      alt={profile.handle}
                    />
                  ) : (
                    <img
                      src={profile.image}
                      alt={profile.handle}
                      className="trusted-carousel-avatar"
                      loading="lazy"
                    />
                  )}
                  <span className="trusted-carousel-handle">{profile.handle}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const belowContent = (
    <div className="success-page">
      <div className="pricing-container">
        <div className="plans-grid">
          <PlanCard
            title="Now Boarding"
            price="$39 / year"
            planId="creator_now_boarding_yearly"
            description={[
              'For creators with 1,000 – 25,000 followers',
              'Access to creator trips and opportunities',
              'Build your travel portfolio with brands'
            ]}
          />
          <PlanCard
            title="In Flight"
            price="$49 / year"
            planId="creator_in_flight_yearly"
            description={[
              'For creators with 25,000+ followers',
              'Premium access to campaigns and partnerships',
              'Priority review for brand collaborations'
            ]}
          />
          <PlanCard
            title="Flight Crew"
            price="$249 / year"
            planId="creator_flight_crew_yearly"
            description={[
              'For talent/management/marketing agencies',
              'Manage multiple creators and submissions',
              'Dedicated partner support'
            ]}
          />
        </div>
        <div className="pricing-footer">
          First Class — Invitation Only for full access and benefits as VIP Postcard Partners
        </div>

        {/* Social proof */}
        <div className="social-proof-grid">
          {[{
            img: '/chloe-creator.png',
            name: 'Chloe, Creator',
            quote: '"As a travel content creator, I’ve worked with a lot of vacation rental hosts — and keeping track of pitches, approvals, comped stays, deliverables, and posting dates used to be a total headache. Using Postaway\'s "My Collab Tracker" feature made the entire collaboration process incredibly smooth."'
          }, {
            img: '/jordan.png',
            name: 'Jordan, Host',
            quote: '"Postaway made finding the right creator to collaborate with incredibly easy. Instead of spending hours searching and vetting influencers, I was matched with a quality content creator whose audience aligned perfectly with my property. The process was smooth, communication was clear, and we saw a noticeable boost in awareness - and even a few direct bookings - shortly after the collaboration."'
          }, {
            img: '/hayden.png',
            name: 'Hayden, Traveler',
            quote: '"I was overwhelmed planning my New Zealand trip, but Postaway made it effortless. I found the perfect itinerary tailored to exactly what I wanted — adventure, scenery, and unique stays all in one seamless plan. It saved me hours of research and made me even more excited for the trip."'
          }].map((t, i) => (
            <div key={i} className="social-proof-card">
              <img src={t.img} alt={t.name} />
              <div>
                <div className="social-proof-name">{t.name}</div>
                <div className="social-proof-quote">{t.quote}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ExitIntentModal />
      <InfoPage
        below={belowContent}
        disableStamps
        belowHeader={header}
        belowTitleCenter
        tallOverride={false}
        customMinHeight={1200}
      />
    </>
  );
};

export default Success;


